import React, { useEffect, useState } from "react";
import { Tree, Modal, Row, Col, Card, Input, Spin, Button, DatePicker, Space } from "antd";
import ReactJson from "@microlink/react-json-view";
import dayjs from "dayjs";
import { getWorkflowInstanceList, getSteptransactionData } from '../../../services/admin/adminService';
import '../assets/styles/common.css';
import Grid from "../components/CommonFields/Grid";
import CustomTree from "../components/CommonFields/Tree";
const { RangePicker } = DatePicker;
const dateFormat = 'YYYY/MM/DD';

const generateUnique4Digit = () => {
    let digits = '0123456789'.split('');
    let result = '';
    for (let i = 0; i < 4; i++) {
        const index = Math.floor(Math.random() * digits.length);
        result += digits[index];
        digits.splice(index, 1); // remove used digit to prevent repeats
    }
    return result;
};


const ConversationLog = () => {
    const [instanceList, setInstanceList] = useState([]);
    const [jsonVisible, setJsonVisible] = useState(false);
    const [selectedJson, setSelectedJson] = useState(null);
    const [instanceData, setInstanceData] = useState(null);
    const [selectedInstanceId, setSelectedInstanceId] = useState('');
    const [selectedStepName, setSelectedStepName] = useState('');
    const [filteredTreeData, setFilteredTreeData] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [conversationId, setconversationId] = useState('');
    const [phoneSearch, setPhoneSearch] = useState('');
    const [fromDate, setFromDate] = useState(dayjs());
    const [toDate, setToDate] = useState(dayjs());

    const handleViewJson = (jsonString) => {
        try {
            setSelectedJson(JSON.parse(jsonString));
            setJsonVisible(true);
        } catch (error) {
            console.error("Invalid JSON:", error);
        }
    };

    const instanceDataColumns = [
        {
            title: "Transaction ID",
            dataIndex: "transactionId",
            key: "transactionId",
            hidden: true
        },
        {
            title: "Step Name",
            dataIndex: "componentName",
            key: "componentName",
        },
        {
            title: "Data JSON",
            key: "dataJson",
            dataIndex: "dataJson",
            render: ["viewjson"],
        },
        {
            title: "Created Date",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value) => dayjs(value).format("MM-DD-YYYY HH:mm:ss"),
        },
    ];

    const loadStepTransactionData = async (transactionId) => {
        try {
            setLoading(true);
            const response = await getSteptransactionData(transactionId);
            const jsonData = response.data;
            if (jsonData.success) {
                const actualData = jsonData.data.transactionData;
                if (actualData?.length > 0) {
                    setInstanceData(actualData);
                }
                else {
                    setInstanceData([]);
                }
            }
        } catch (error) {
            console.error('Error fetching location data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getInstanceList = async (wfId, conversationId, from = fromDate, to = toDate) => {
        try {
            setFilteredTreeData([]);
            setInstanceData([]);
            setLoading(true);
            const response = await getWorkflowInstanceList(wfId, conversationId, from.format(dateFormat), to.format(dateFormat));
            const jsonData = response.data;
            if (jsonData.success) {
                const actualData = jsonData?.data?.instance;
                if (actualData?.length > 0) {
                    setInstanceList(actualData);
                    const tree = buildTreeData(actualData);
                    setFilteredTreeData(tree);
                }
                else {
                    setFilteredTreeData([]);
                }
            }
        } catch (error) {
            console.error('Error fetching instance data:', error);
        }
        finally {
            setLoading(false);
        }
    };

    const loadAllInstance = () => {
        getInstanceList("", "");
    }

    useEffect(() => {
        loadAllInstance();
    }, []);

    const buildTreeData = (data, search = '') => {
        return data
            .map((instance) => {
                let stepsArray = [];
                try {
                    stepsArray = Array.isArray(instance.steps)
                        ? instance.steps
                        : JSON.parse(instance.steps);
                } catch (e) {
                    console.error('Error parsing steps:', e);
                }

                const filteredSteps = stepsArray.filter((step) =>
                    step.componentName.toLowerCase().includes(search.toLowerCase()) ||
                    step.transactionId.toLowerCase().includes(search.toLowerCase())
                );

                const matchInstance = instance.instanceId.toLowerCase().includes(search.toLowerCase());

                if (filteredSteps.length === 0 && !matchInstance) return null;

                return {
                    title: (
                        <div className="instance-node">
                            <b>{instance.clientPhone || "Unidentified"} </b>
                            <div><small>{dayjs(instance.createdAt).format('MM-DD-YYYY HH:mm:ss')}</small></div>
                            {/* <small>{instance.instanceId}</small> */}
                        </div>
                    ),
                    key: `${instance.instanceId}`,
                    children: filteredSteps.map((step) => ({
                        title: (
                            <div className="step-node">
                                <div className="step-content-inline">
                                    <span className="step-name">{step.componentName}</span>
                                    <span className="step-date">{step.createdAt}</span>
                                </div>
                            </div>

                        ),
                        key: `${step.transactionId}_${step.stepId}_${generateUnique4Digit()}`,
                        data: {
                            instanceId: instance.instanceId,
                            stepId: step.stepId,
                            transactionId: step.transactionId,
                            componentName: step.componentName,
                        },
                        isLeaf: true,
                    })),
                };
            })

            .filter(Boolean);
    };

    const onSelect = (selectedKeys, info) => {
        setInstanceData([]);
        if (info.node.isLeaf) {
            const { instanceId, stepId, transactionId, componentName } = info.node.data;
            setSelectedInstanceId(instanceId);
            setSelectedStepName(componentName);
            loadStepTransactionData(transactionId);
        }
    };

    const handleInstanceBySearch = () => {
        getInstanceList("", conversationId);
    }

    const handleInstanceBySearchClear = () => {
        const from = dayjs();
        const to = dayjs();
        setFromDate(from);
        setToDate(to);
        setconversationId('');
        setPhoneSearch('');
        setSelectedInstanceId("");
        setSelectedStepName("");
        getInstanceList("", "", from, to);
    }

    const handleSearchInstance = (searchText) => {
        if (!searchText) {
            const tree = buildTreeData(instanceList);
            setFilteredTreeData(tree);
            setExpandedKeys(tree.map(item => item.key));
            return;
        }

        const filtered = instanceList.filter(item =>
            (item.clientPhone || '').toLowerCase().includes(searchText.toLowerCase())
        );

        const tree = buildTreeData(filtered);
        setFilteredTreeData(tree);
        // setExpandedKeys(tree.map(item => item.key));
    };

    const handleRangeChange = (dates) => {
        if (dates) {
            setFromDate(dates[0]);
            setToDate(dates[1]);
        } else {
            setFromDate(dayjs());
            setToDate(dayjs());
        }
    };

    return (
        <div className="conversation-log-container">
            <Row
                gutter={16}
                justify="end"
                align="middle"
                style={{ padding: '2px 16px 0', marginBottom: 0 }}
            >
                {/* <Col>
                    <Input
                        placeholder="Search Phone Number"
                        allowClear
                        value={phoneSearch}
                        onChange={(e) => setPhoneSearch(e.target.value)}
                        style={{ width: 400 }}
                    />
                </Col> */}
                <Col>
                    <Input
                        placeholder="Search Conversation ID"
                        allowClear
                        //onClear={loadAllInstance()}
                        value={conversationId}
                        onChange={(e) => setconversationId(e.target.value)}
                        style={{ width: 400 }}
                    />
                </Col>
                <Col>
                    <Space direction="horizontal" size={12}>
                        <RangePicker
                            value={[fromDate, toDate]}
                            onChange={handleRangeChange}
                        />
                    </Space></Col>
                <Col>
                    <Button type="primary" onClick={handleInstanceBySearch}>
                        Search
                    </Button>
                </Col>
                <Col>
                    <Button type="primary" onClick={handleInstanceBySearchClear}>
                        Clear
                    </Button>
                </Col>
            </Row>

            <Row gutter={16} style={{ padding: '16px' }}>
                <Col span={7}>
                    <Card
                        title={
                            <div className="card-header">
                                <div className="card-title">Conversastion Log(s)</div>
                                <Input
                                    placeholder="Search Phone"
                                    allowClear
                                    onChange={(e) => handleSearchInstance(e.target.value)}
                                    style={{ marginTop: 12 }}
                                />
                            </div>
                        }
                        className="tree-card"
                    >
                        <Spin spinning={loading}>
                            {/* <Tree
                                treeData={filteredTreeData}
                                onSelect={onSelect}
                                expandedKeys={expandedKeys}
                                onExpand={setExpandedKeys}
                                showLine={{ showLeafIcon: false }}
                                showIcon={false}
                                className="workflow-tree"
                            /> */}

                            <CustomTree
                                defaultExpandAll
                                defaultExpandedKeys={0}
                                onSelect={onSelect}
                                treeData={filteredTreeData}
                                className="workflow-tree custom-tree"
                            />

                        </Spin>
                    </Card>
                </Col>

                {/* Right Panel */}
                <Col span={17}>
                    <Card
                        title={
                            <div className="data-card-header">
                                <span>
                                    {selectedInstanceId ? (
                                        <>
                                            {selectedStepName && (
                                                <span className="step-title"><b>{selectedStepName}</b></span>
                                            )}
                                            <span className="instance-title" style={{ fontSize: "12px" }}> (ins-id: {selectedInstanceId})</span>
                                        </>
                                    ) : (
                                        "Select an Instance"
                                    )}
                                </span>
                            </div>
                        }
                        className="data-card"
                    >
                        <Grid
                            columnData={{ columns: instanceDataColumns }}
                            tableData={instanceData}
                            rowKey="transactionId"
                            searchButton={true}
                            downloadCSV={true}
                            showEdit={false}
                            showDelete={false}
                            showView={true}
                            handleViewJson={(record) => handleViewJson(record?.dataJson)}
                            csvFileName="StepData"
                            paginationConfig={{ pageSizeOptions: ['10', '20', '50', '100'] }}
                            className="custom-grid"
                            createButton={false}
                            loading={loading}
                        />

                        <Modal
                            title="JSON Data"
                            open={jsonVisible}
                            onCancel={() => setJsonVisible(false)}
                            footer={null}
                            width="90%"
                            style={{ maxWidth: 700 }}
                            className="json-modal"
                        >
                            {selectedJson && (
                                <ReactJson
                                    src={selectedJson}
                                    theme="chalk"
                                    name={false}
                                    collapsed={false}
                                    enableClipboard={false}
                                    displayDataTypes={false}
                                    indentWidth={4}
                                    style={{ fontSize: '14px', wordBreak: 'break-word' }}
                                />
                            )}
                        </Modal>
                    </Card>
                </Col>
            </Row>
        </div>

    );
};

export default ConversationLog;